import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { IoPlayCircleOutline } from 'react-icons/io5';
import { RxCross2 } from 'react-icons/rx';
import { LuVolume2, LuVolumeOff } from 'react-icons/lu';
import { FaCirclePlus, FaCircleMinus } from 'react-icons/fa6';
import { IoIosEye, IoIosEyeOff } from 'react-icons/io';
import { useAuth } from '../contexts/AuthContext';
import * as watchlist from '../api/watchlist';
import * as pagesApi from '../api/pagesStore';
import { toEmbedAutoplay } from '../api/filemoon';

const apiBase = process.env.REACT_APP_PAGES_API || '/api';

export default function SeriesPopupPreviewOverlay() {
  const location = useLocation();
  const history = useHistory();
  const params = useMemo(() => new URLSearchParams(location.search || ''), [location.search]);
  const [previewParam, setPreviewParam] = useState(params.get('preview'));
  const [list, setList] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [previewSeasonsOpen, setPreviewSeasonsOpen] = useState(false);
  const [previewVideoActive, setPreviewVideoActive] = useState(false);
  const [previewVideoMuted, setPreviewVideoMuted] = useState(false);
  const previewVideoRef = useRef(null);
  const [closeHover, setCloseHover] = useState(false);
  const [wlHover, setWlHover] = useState(false);
  const [followHover, setFollowHover] = useState(false);
  const [playHover, setPlayHover] = useState(false);

  const previewOpen = !!previewParam && previewParam !== 'draft';
  const { user, followedMap = {}, toggleFollow } = useAuth();
  const [inWatchlist, setInWatchlist] = useState(false);
  const [wlPulse, setWlPulse] = useState(''); // 'add' | 'remove' | ''
  const [inFollowing, setInFollowing] = useState(false);

  const previewItem = useMemo(() => {
    if (!previewOpen) return null;
    const found = (list||[]).find(p => String(p.id) === String(previewParam));
    return found || null;
  }, [previewOpen, list, previewParam]);

  // Treat as film either when explicitly marked or when there's only one item and max 1 season
  const isFilm = !!(previewItem && (
    String(previewItem.contentType || '') === 'film' ||
    (((previewItem.seasons || 0) <= 1) && ((previewItem.episodes || []).length <= 1))
  ));

  function genPlayId() {
    // 9-digit random numeric id
    const n = Math.floor(100000000 + Math.random() * 900000000);
    return String(n);
  }

  useEffect(() => {
    if (!previewOpen) return;
    (async () => {
      try {
        const r = await fetch(`${apiBase}/popups`);
        const j = await r.json();
        setList(Array.isArray(j) ? j : []);
      } catch {}
    })();
  }, [previewOpen]);

  // watchlist state: initialize from cache immediately, then confirm
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const id = previewParam;
        if (!user || !id) { if (mounted) setInWatchlist(false); return; }
        // fast path from cache
        const cached = await watchlist.isIn(user.uid, id);
        if (mounted) setInWatchlist(!!cached);
        // ensure cache hydrated (no-op if fresh)
        await watchlist.getAll(user.uid);
        const present = await watchlist.isIn(user.uid, id);
        if (mounted) setInWatchlist(!!present);
      } catch {}
    })();
    const onChanged = (e) => {
      const id = e?.detail?.popupId;
      if (!id || String(id) !== String(previewParam)) return;
      const action = e?.detail?.action;
      setInWatchlist(prev => action === 'add' ? true : action === 'remove' ? false : prev);
      setWlPulse(action === 'add' ? 'add' : action === 'remove' ? 'remove' : '');
      if (action) setTimeout(() => setWlPulse(''), 350);
    };
    window.addEventListener('watchlist:changed', onChanged);
    return () => { mounted = false; window.removeEventListener('watchlist:changed', onChanged); };
  }, [user?.uid, previewParam]);

  // follow state from context
  useEffect(() => {
    const pid = String(previewParam || '');
    setInFollowing(!!(pid && followedMap[pid]));
  }, [followedMap, previewParam]);

  // listen to custom open events and browser back/forward to keep in sync without Router rerender
  useEffect(() => {
    function onOpen(ev) {
      const id = ev && ev.detail && ev.detail.id;
      if (!id) return;
      setPreviewParam(String(id));
    }
    function onPop() {
      const p = new URLSearchParams(window.location.search || '');
      setPreviewParam(p.get('preview'));
    }
    window.addEventListener('preview:open', onOpen);
    window.addEventListener('popstate', onPop);
    // notify that overlay is ready to receive preview:open events (useful on cold loads)
    try { setTimeout(() => { try { window.dispatchEvent(new CustomEvent('preview:ready')); } catch {} }, 0); } catch {}
    return () => {
      window.removeEventListener('preview:open', onOpen);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  // Freeze background scroll while overlay is open WITHOUT layout shift
  // Lock both <html> and <body> to avoid interacting with underlying page scrollbar
  const scrollLockRef = useRef({ y: 0, applied: false, pr: '', htmlOverflow: '', bodyPos: '', bodyTop: '', bodyWidth: '', bodyPR: '' });
  useEffect(() => {
    if (!previewOpen) {
      // unlock if needed
      if (scrollLockRef.current.applied) {
        const { y, htmlOverflow, bodyPos, bodyTop, bodyWidth, bodyPR } = scrollLockRef.current;
        try { document.documentElement.classList.remove('modal-open'); } catch {}
        if (document.documentElement) document.documentElement.style.overflow = htmlOverflow || '';
        document.body.style.position = bodyPos || '';
        document.body.style.top = bodyTop || '';
        document.body.style.width = bodyWidth || '';
        document.body.style.paddingRight = bodyPR || '';
        window.scrollTo(0, y || 0);
        scrollLockRef.current.applied = false;
      }
      return;
    }
    // lock
    try {
      const y = window.scrollY || window.pageYOffset || 0;
      const sbw = window.innerWidth - document.documentElement.clientWidth;
      const prevHtmlOverflow = document.documentElement ? document.documentElement.style.overflow : '';
      const prevBodyPos = document.body.style.position;
      const prevBodyTop = document.body.style.top;
      const prevBodyWidth = document.body.style.width;
      const prevBodyPR = document.body.style.paddingRight;

      // Hide root scrollbar to prevent interacting with it
      if (document.documentElement) {
        document.documentElement.style.overflow = 'hidden';
        try { document.documentElement.classList.add('modal-open'); } catch {}
      }
      // Preserve layout and lock body in place
      document.body.style.position = 'fixed';
      document.body.style.top = `-${y}px`;
      document.body.style.width = '100%';
      if (sbw > 0) document.body.style.paddingRight = `${sbw}px`;
      scrollLockRef.current = { y, applied: true, pr: prevBodyPR, htmlOverflow: prevHtmlOverflow, bodyPos: prevBodyPos, bodyTop: prevBodyTop, bodyWidth: prevBodyWidth, bodyPR: prevBodyPR };
    } catch {}
    return () => {
      // ensure unlock on unmount/change
      if (scrollLockRef.current.applied) {
        const { y, htmlOverflow, bodyPos, bodyTop, bodyWidth, bodyPR } = scrollLockRef.current;
        try { document.documentElement.classList.remove('modal-open'); } catch {}
        if (document.documentElement) document.documentElement.style.overflow = htmlOverflow || '';
        document.body.style.position = bodyPos || '';
        document.body.style.top = bodyTop || '';
        document.body.style.width = bodyWidth || '';
        document.body.style.paddingRight = bodyPR || '';
        window.scrollTo(0, y || 0);
        scrollLockRef.current.applied = false;
      }
    };
  }, [previewOpen]);

  // schedule video fade-in
  useEffect(() => {
    let t;
    setPreviewVideoActive(false);
    setPreviewVideoMuted(false);
    if (previewOpen && previewItem && previewItem.previewHasVideo && previewItem.previewVideoUrl) {
      t = setTimeout(() => setPreviewVideoActive(true), 2000);
    }
    return () => {
      if (t) clearTimeout(t);
      try { if (previewVideoRef.current) previewVideoRef.current.pause(); } catch {}
    };
  }, [previewOpen, previewItem]);

  useEffect(() => {
    if (!previewVideoActive) return;
    const v = previewVideoRef.current;
    if (!v) return;
    v.muted = false;
    v.play().catch(() => { v.muted = true; setPreviewVideoMuted(true); v.play().catch(()=>{}); });
  }, [previewVideoActive]);

  function closeOverlay() {
    setPreviewSeasonsOpen(false);
    const p = new URLSearchParams(window.location.search || '');
    p.delete('preview');
    const newUrl = `${window.location.pathname}${p.toString() ? `?${p.toString()}` : ''}`;
    window.history.pushState({}, '', newUrl);
    window.dispatchEvent(new CustomEvent('preview:closed'));
    setPreviewSeasonsOpen(false);
    setPreviewParam(null);
    // unlock scroll immediately
    try {
      if (scrollLockRef.current.applied) {
        const { y, htmlOverflow, bodyPos, bodyTop, bodyWidth, bodyPR } = scrollLockRef.current;
        try { document.documentElement.classList.remove('modal-open'); } catch {}
        if (document.documentElement) document.documentElement.style.overflow = htmlOverflow || '';
        document.body.style.position = bodyPos || '';
        document.body.style.top = bodyTop || '';
        document.body.style.width = bodyWidth || '';
        document.body.style.paddingRight = bodyPR || '';
        window.scrollTo(0, y || 0);
        scrollLockRef.current.applied = false;
      }
    } catch {}
  }

  if (!previewOpen || !previewItem) return null;

  return (
    <div role="dialog" aria-modal="true" tabIndex={-1} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2147483000, display:'flex', alignItems:'flex-start', justifyContent:'center', overflowY:'auto', padding:'36px 0', overscrollBehavior:'contain', WebkitOverflowScrolling:'touch' }} onClick={closeOverlay}>
      <div style={{ width:'62%', maxWidth:760, margin:'0 auto', background:'#071014', borderRadius:12, overflow:'visible', color:'#fff', position:'relative', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }} onClick={e=>e.stopPropagation()}>
        <button
          aria-label="Zavřít"
          onClick={closeOverlay}
          onMouseEnter={() => setCloseHover(true)}
          onMouseLeave={() => setCloseHover(false)}
          style={{
            position:'absolute', top:12, right:12, width:46, height:46, borderRadius:'50%', border:'none',
            background: closeHover ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:5, cursor:'pointer',
            transition:'background .18s ease, transform .12s ease, box-shadow .18s ease',
            transform: closeHover ? 'scale(1.08)' : 'scale(1)',
            boxShadow: closeHover ? '0 12px 30px rgba(0,0,0,0.6)' : '0 6px 16px rgba(0,0,0,0.45)'
          }}
        >
          <RxCross2 color="#fff" size={20} />
        </button>

  <div style={{ position:'relative', height: isFilm ? 380 : 240 }}>
          <div style={{ position:'absolute', inset:0, backgroundImage:`url(${previewItem.heroImage})`, backgroundSize:'cover', backgroundPosition:'center', opacity: previewVideoActive ? 0 : 1, transition:'opacity .4s ease' }} />
          {previewItem.previewHasVideo && previewItem.previewVideoUrl && (
            <div style={{ position:'absolute', inset:0, opacity: previewVideoActive ? 1 : 0, transition:'opacity .4s ease' }}>
              <video
                ref={previewVideoRef}
                src={previewItem.previewVideoUrl}
                style={{ width:'100%', height:'100%', objectFit:'cover' }}
                playsInline
                autoPlay
                muted={previewVideoMuted}
                onEnded={() => setPreviewVideoActive(false)}
              />
              <button
                aria-label={previewVideoMuted ? 'Zapnout zvuk' : 'Vypnout zvuk'}
                onClick={(e) => {
                  e.stopPropagation();
                  const v = previewVideoRef.current; if (!v) return;
                  if (previewVideoMuted) { v.muted = false; setPreviewVideoMuted(false); v.play().catch(()=>{}); }
                  else { v.muted = true; setPreviewVideoMuted(true); }
                }}
                style={{ position:'absolute', right:12, bottom:12, width:36, height:36, borderRadius:'50%', border:'1px solid rgba(255,255,255,0.35)', background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer' }}
              >
                {previewVideoMuted ? <LuVolumeOff color="#fff" size={18} /> : <LuVolume2 color="#fff" size={18} />}
              </button>
            </div>
          )}
          <div style={{ position:'absolute', left:20, bottom:16, right:20 }}>
            {previewItem.seriesLogo && <img src={previewItem.seriesLogo} alt="" style={{ height:44 }} />}
            <h2 style={{ margin:6 }}>{previewItem.title}</h2>
            {!isFilm && (
              <div style={{ opacity:0.9 }}>{previewItem.year} • {previewItem.seasons} řad</div>
            )}
            <div style={{ marginTop:10, display:'flex', gap:8 }}>
              <button
                onMouseEnter={() => setPlayHover(true)}
                onMouseLeave={() => setPlayHover(false)}
                style={{
                  background: playHover ? '#e6e6e6' : '#fff',
                  color:'#000', border:'none', padding:'10px 16px', borderRadius:999, fontWeight:700, cursor:'pointer',
                  transition: 'background .12s ease, box-shadow .12s ease',
                  boxShadow: playHover ? '0 10px 24px rgba(0,0,0,0.45)' : 'none'
                }}
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const seasonNum = selectedSeason || 1;
                    let ep;
                    if (isFilm) {
                      ep = (previewItem.episodes||[])[0];
                    } else {
                      const eps = (previewItem.episodes||[]).filter(x => (x.season||1)===seasonNum);
                      ep = eps[0];
                    }
                    if (!ep) return;
                    if (ep.playId) {
                      window.location.href = `/play/${ep.playId}`;
                      return;
                    }
                    if (!ep.videoUrl) return;
                    const url = toEmbedAutoplay(ep.videoUrl);
                    const id = genPlayId();
                    const path = `/play/${id}`;
                    const title = isFilm ? (previewItem.title || 'Přehrát film') : `${previewItem.title || 'Přehrát'} — S${ep.season || seasonNum}E${ep.index || 1}`;
                    await pagesApi.addPage({ path, title, content: JSON.stringify({ url, popupId: String(previewParam||''), contentType: (isFilm ? 'film' : 'serial'), showTitle: previewItem.title || 'Přehrát', epTitle: isFilm ? (ep.title || previewItem.title || 'Film') : (ep.title || `S${ep.season || seasonNum}E${ep.index || 1}`), season: isFilm ? null : (ep.season || seasonNum), index: isFilm ? null : (ep.index || 1), img: previewItem.listThumb || previewItem.heroImage || '' }) });
                    // update local list state for this preview item
                    setList(arr => arr.map(p => {
                      if (String(p.id) !== String(previewParam)) return p;
                      const epsAll = Array.isArray(p.episodes) ? p.episodes.map(e => {
                        if ((e.season||1)===(ep.season||1) && (e.index||0)===(ep.index||0) && (e.title||'')===(ep.title||'')) return { ...e, playId: id, videoUrl: url };
                        return e;
                      }) : p.episodes;
                      return { ...p, episodes: epsAll };
                    }));
                    window.location.href = path;
                  } catch (_) {}
                }}
              >{isFilm ? 'Přehrát film' : 'Přehrát 1. díl'}</button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!user) { alert('Přihlas se pro použití watchlistu.'); return; }
                  const pid = String(previewParam);
                  try {
                    // optimistic UI: flip immediately
                    const next = !inWatchlist;
                    setInWatchlist(next);
                    // fire pulse and event instantly
                    try { window.dispatchEvent(new CustomEvent('watchlist:changed', { detail: { popupId: pid, action: next ? 'add' : 'remove' } })); } catch {}
                    setWlPulse(next ? 'add' : 'remove');
                    setTimeout(() => setWlPulse(''), 350);

                    if (!next) {
                      await watchlist.remove(user.uid, pid);
                    } else {
                      await watchlist.add(user.uid, {
                        popupId: pid,
                        title: previewItem.title || '',
                        img: previewItem.listThumb || previewItem.heroImage || '',
                        link: `${window.location.pathname}?preview=${encodeURIComponent(pid)}`
                      });
                    }
                  } catch (err) {
                    console.error('watchlist toggle failed', err);
                    alert('Nepodařilo se změnit watchlist.');
                    // revert optimistic change
                    setInWatchlist(v => !v);
                  }
                }}
                title={inWatchlist ? 'Odebrat z watchlistu' : 'Přidat do watchlistu'}
                onMouseEnter={() => setWlHover(true)}
                onMouseLeave={() => setWlHover(false)}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  border: 'none',
                  background: inWatchlist ? '#d03535' : '#2d8a34',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: wlPulse === 'add' ? '0 0 0 6px rgba(45,138,52,0.35)' : wlPulse === 'remove' ? '0 0 0 6px rgba(208,53,53,0.35)' : (wlHover ? '0 8px 22px rgba(0,0,0,0.45)' : 'none'),
                  transition: 'box-shadow 180ms ease, background .12s ease'
                }}
              >
                {inWatchlist ? <FaCircleMinus color="#fff" size={18} /> : <FaCirclePlus color="#fff" size={18} />}
              </button>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!user) { alert('Přihlas se pro použití sledování.'); return; }
                  const pid = String(previewParam);
                  try {
                    const next = !inFollowing;
                    setInFollowing(next);
                    await toggleFollow(pid, next);
                  } catch (err) {
                    console.error('follow toggle failed', err);
                    alert('Nepodařilo se změnit sledování.');
                    setInFollowing(v => !v);
                  }
                }}
                title={inFollowing ? 'Přestat sledovat' : 'Sledovat'}
                onMouseEnter={() => setFollowHover(true)}
                onMouseLeave={() => setFollowHover(false)}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  border: 'none',
                  background: inFollowing ? '#1e3a8a' : '#2563eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: followHover ? '0 8px 22px rgba(0,0,0,0.45)' : 'none',
                  transition: 'box-shadow .12s ease, background .12s ease'
                }}
              >
                {inFollowing ? <IoIosEyeOff color="#fff" size={18} /> : <IoIosEye color="#fff" size={18} />}
              </button>
            </div>
            {!isFilm && (() => {
              const genres = Array.isArray(previewItem.genres) ? previewItem.genres.slice(0,3) : [];
              if (!genres.length) return null;
              return (
                <div style={{ marginTop:6, opacity:0.9, fontSize:14 }}>
                  {genres.join(', ')}
                </div>
              );
            })()}
          </div>
        </div>

        {isFilm && (
          <div style={{ padding:12 }}>
            {(() => {
              const mins = Number(previewItem.runtimeMins)||0;
              const hours = Math.floor(mins/60);
              const minsLeft = mins%60;
              const runtimeStr = mins>0 ? `${hours>0?`${hours} h `:''}${minsLeft>0?`${minsLeft} min`:hours>0?'':''}` : '';
              const genres = Array.isArray(previewItem.genres) ? previewItem.genres.slice(0,3) : [];
              const overview = (previewItem.overview||'').trim();
              return (
                <div style={{ background:'#0e141a', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, padding:'12px 14px' }}>
                  <div style={{ opacity:0.9, fontSize:14, marginBottom:6 }}>
                    <span>{previewItem.year}</span>
                    {runtimeStr && <span> • {runtimeStr}</span>}
                    {!!genres.length && <span> • {genres.join(', ')}</span>}
                  </div>
                  {!!overview && <div style={{ fontSize:14, lineHeight:1.4, opacity:0.95 }}>{overview}</div>}
                </div>
              );
            })()}
          </div>
        )}

        {!isFilm && (
        <div style={{ padding:12 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ position:'relative' }}>
              <button style={{ background:'#234', color:'#cde', border:'none', padding:'10px 16px', borderRadius:6, cursor:'pointer', fontSize:15 }} onClick={()=>setPreviewSeasonsOpen(v=>!v)}>Řada {selectedSeason}</button>
              {previewSeasonsOpen && (
                <div style={{ position:'absolute', top:'100%', left:0, background:'#0b0e10', border:'1px solid #1f2830', borderRadius:8, overflowY:'auto', zIndex:2147483001, minWidth:220, maxHeight:'60vh', boxShadow:'0 16px 40px rgba(0,0,0,0.45)' }}>
                  {Array.from({ length: Math.max(1, Number(previewItem.seasons)||1) }).map((_, si) => {
                    const seasonNum = si + 1;
                    const count = (previewItem.episodes||[]).filter(ep => (ep.season||1)===seasonNum).length;
                    return (
                      <div key={si}>
                        <button style={{ background: seasonNum===selectedSeason ? '#233' : '#0b0e10', color:'#cde', border:'none', padding:'10px 12px', width:'100%', textAlign:'left', fontSize:15, display:'flex', gap:8, alignItems:'baseline', cursor:'pointer' }} onClick={()=>{ setSelectedSeason(seasonNum); setPreviewSeasonsOpen(false); }}>
                          <span style={{ fontWeight:800 }}>Řada {seasonNum}</span>
                          <span style={{ opacity:0.8, fontSize:14 }}>({count} dílů)</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ marginTop:12 }}>
            <style>{`
              .ep-row { display:flex; gap:16px; padding:16px 0; align-items:center; text-decoration:none; color:#fff; }
              .ep-row.clickable { cursor: pointer; }
              .ep-row + .ep-row { border-top: 1px solid rgba(255,255,255,0.08); }
              .ep-num { width:42px; flex:0 0 42px; text-align:center; color:rgba(255,255,255,0.6); font-weight:700; font-size:24px; }
              .ep-thumb { position:relative; width:160px; height:90px; flex:0 0 160px; border-radius:8px; overflow:hidden; background-size:cover; background-position:center; }
              .ep-thumb .play-overlay { position:absolute; inset:0; display:flex; align-items:center; justify-content:center; opacity:0; transition:opacity .15s ease; background:linear-gradient(0deg, rgba(0,0,0,0.18), rgba(0,0,0,0.18)); }
              .ep-row:hover .ep-thumb .play-overlay { opacity:1; }
              .ep-text .ep-title { font-weight:800; font-size:17px; }
              .ep-text .ep-desc { font-size:14px; opacity:0.92; }
            `}</style>
            {(() => {
              const seasonEps = (previewItem.episodes||[]).filter(ep => (ep.season||1)===selectedSeason);
              return seasonEps.map((ep, i) => {
                     const href = ep.playId ? `/play/${ep.playId}` : undefined;
                const RowTag = href ? 'a' : 'div';
                const onClick = async (e) => {
                  if (href) return; // default navigation
                  if (!ep.videoUrl) return;
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    const url = toEmbedAutoplay(ep.videoUrl);
                    const id = genPlayId();
                    const path = `/play/${id}`;
                    const title = `${previewItem.title || 'Přehrát'} — S${ep.season || selectedSeason}E${ep.index || (i+1)}`;
                    await pagesApi.addPage({ path, title, content: JSON.stringify({ url }) });
                    // update local list state for this preview item
                    setList(arr => arr.map(p => {
                      if (String(p.id) !== String(previewParam)) return p;
                      const epsAll = Array.isArray(p.episodes) ? p.episodes.map(e => {
                        if ((e.season||1)===(ep.season||1) && (e.index||0)===(ep.index||0) && (e.title||'')===(ep.title||'')) return { ...e, playId: id, videoUrl: url };
                        return e;
                      }) : p.episodes;
                      return { ...p, episodes: epsAll };
                    }));
                    window.location.href = path;
                  } catch (_) {}
                };
                const common = { className:`ep-row ${href ? 'clickable' : ''}`.trim(), href, onClick };
                return (
                  <RowTag key={i} {...common}>
                    <div className="ep-num">{i+1}</div>
                    <div className="ep-thumb" style={{ backgroundImage:`url(${ep.thumb})` }}>
                      <div className="play-overlay">
                        <IoPlayCircleOutline color="#fff" size={54} />
                      </div>
                    </div>
                    <div className="ep-text">
                      <div className="ep-title">{ep.title}</div>
                      <div className="ep-desc">{ep.desc}</div>
                    </div>
                  </RowTag>
                );
              });
            })()}
          </div>
  </div>
  )}
      </div>
    </div>
  );
}
